import React from 'react';
import { Search, Calendar, Filter, RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export interface CampaignFiltersBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedBrand: string;
  onBrandChange: (value: string) => void;
  brandsList: { id: string; name: string }[];
  selectedOwner: string;
  onOwnerChange: (value: string) => void;
  ownersList: string[];
  dateRange: string;
  onDateRangeChange: (value: string) => void;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  isBrandOrg?: boolean;
}

export const CampaignFiltersBar: React.FC<CampaignFiltersBarProps> = ({
  searchTerm,
  onSearchChange,
  selectedBrand,
  onBrandChange,
  brandsList,
  selectedOwner,
  onOwnerChange,
  ownersList,
  dateRange,
  onDateRangeChange,
  onResetFilters,
  hasActiveFilters,
  isBrandOrg = false,
}) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-3.5">
      {/* Search Bar */}
      <div className="flex-1 max-w-md relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 transform -translate-y-1/2" />
        <Input
          type="text"
          placeholder={
            isBrandOrg
              ? "Search campaigns by name or status..."
              : "Search campaigns by name, brand, or status..."
          }
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 h-10 bg-white border-gray-200 focus:border-purple-500 rounded-lg text-sm"
        />
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Brand Dropdown */}
        {!isBrandOrg && (
          <Select value={selectedBrand} onValueChange={onBrandChange}>
            <SelectTrigger className="w-[140px] h-10 bg-white border-gray-200 text-gray-700 text-sm font-medium rounded-lg">
              <SelectValue placeholder="All Brands" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Brands</SelectItem>
              {brandsList.map((brand) => (
                <SelectItem key={brand.id} value={brand.name}>
                  {brand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Owner Dropdown */}
        <Select value={selectedOwner} onValueChange={onOwnerChange}>
          <SelectTrigger className="w-[140px] h-10 bg-white border-gray-200 text-gray-700 text-sm font-medium rounded-lg">
            <SelectValue placeholder="All Owners" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Owners</SelectItem>
            {ownersList.map((owner) => (
              <SelectItem key={owner} value={owner}>
                {owner}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range Dropdown */}
        <Select value={dateRange} onValueChange={onDateRangeChange}>
          <SelectTrigger className="w-[160px] h-10 bg-white border-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
            <SelectValue placeholder="Last 30 Days" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="30">Last 30 Days</SelectItem>
            <SelectItem value="60">Last 60 Days</SelectItem>
            <SelectItem value="90">Last 90 Days</SelectItem>
            <SelectItem value="365">This Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>

        {/* Reset Button - Only rendered when a filter is active */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={onResetFilters}
            className="h-10 px-3 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 rounded-lg text-sm flex items-center gap-1.5"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset</span>
          </Button>
        )}
      </div>
    </div>
  );
};


